/**
 * Utility functions for calculating effective stats with modifiers
 */

import useMetadataStore from '@store/useMetadataStore';

/**
 * Get the base value of a stat from the unit
 */
export function getBaseStat(unit, statName) {
  if (!unit) return 0;
  
  // Direct unit properties
  if (['hp', 'ap', 'movement'].includes(statName)) {
    if (unit[statName] === undefined || unit[statName] === null) {
      console.warn(`[getBaseStat] Missing stat '${statName}' on unit`, unit);
      return 0; // Return 0 but warn
    }
    return unit[statName];
  }
  
  // Template properties - note we use unit_template not unitTemplate
  const templateStats = [
    'max_hp', 'max_ap', 'max_movement',
    'strength', 'armor', 
    'agility', 'psi', 'will', 'vision'
  ];
  
  if (templateStats.includes(statName)) {
    // Map current values to max values if needed
    let templateField = statName;
    if (statName === 'hp') templateField = 'max_hp';
    if (statName === 'ap') templateField = 'max_ap';
    if (statName === 'movement') templateField = 'max_movement';
    
    if (!unit.unit_template || unit.unit_template[templateField] === undefined) {
      console.warn(`[getBaseStat] Missing template field '${templateField}' on unit`, unit);
      return 0; // Return 0 but warn
    }
    return unit.unit_template[templateField];
  }
  
  return 0;
}

/**
 * Get all active modifiers for a specific stat
 */
export function getActiveModifiers(unit, statName) {
  if (!unit?.active_effects || !Array.isArray(unit.active_effects)) {
    return [];
  }
  
  const modifiers = [];
  unit.active_effects.forEach(effect => {
    // Hydrate effect with metadata if needed
    useMetadataStore.getState().hydrateUnitEffect(effect);
    // Check for stat modifiers first
    if (effect.stat_modifiers?.[statName]) {
      // Validate source
      if (!effect.source) {
        console.warn('[getActiveModifiers] Missing source for effect:', effect);
      }
      
      // Validate is_permanent field
      if (effect.is_permanent === undefined || effect.is_permanent === null) {
        console.warn('[getActiveModifiers] Missing is_permanent for effect:', effect);
      }
      
      // Only validate turns_remaining if NOT permanent
      if (!effect.is_permanent) {
        if (effect.turns_remaining === undefined || effect.turns_remaining === null) {
          console.warn('[getActiveModifiers] Missing turns_remaining for non-permanent effect:', effect);
        }
      }
      
      // Extract the actual value from the modifier object
      // stat_modifiers contains objects like {value: -1, type: 'flat'}
      const modifierData = effect.stat_modifiers[statName];
      const modifierValue = typeof modifierData === 'object' ? modifierData.value : modifierData;
      
      modifiers.push({
        value: modifierValue,
        source: effect.source,
        effectId: effect.effect_id,  // Add effect ID for icon display
        effectName: effect.effect_name || 'Unknown Effect',  // Keep for backwards compatibility
        duration: effect.turns_remaining,  // Will be undefined for permanent effects (OK!)
        max_turns: effect.max_turns,  // Maximum duration for X/Y display
        unlimited: effect.is_permanent,
        counter_state: effect.counter_state || null  // Include counter state for counter effects
      });
    }
    // Special case for HP: also include DOT/HOT effects based on category and properties
    else if (statName === 'hp' && effect.effect_category && effect.properties) {
      // Check if this is a DOT or HOT effect
      if (effect.effect_category === 'dot' && effect.properties.hp_damage_per_turn !== undefined) {
        // DOT effect with HP damage
        const damage = effect.properties.hp_damage_per_turn;
        if (damage === undefined || damage === null) {
          console.warn('[getActiveModifiers] Missing hp_damage_per_turn for DOT effect:', effect);
        } else if (damage !== 0) {
          modifiers.push({
            value: -damage,  // Negative because it's damage
            source: effect.source,
            effectId: effect.effect_id,
            effectName: effect.effect_name || 'Unknown Effect',
            duration: effect.turns_remaining,
            max_turns: effect.max_turns,  // Maximum duration for X/Y display
            unlimited: effect.is_permanent,
            counter_state: effect.counter_state || null  // Include counter state for counter effects
          });
        }
      } else if (effect.effect_category === 'hot' && effect.properties.hp_healing_per_turn !== undefined) {
        // HOT effect with HP healing
        const healing = effect.properties.hp_healing_per_turn;
        if (healing === undefined || healing === null) {
          console.warn('[getActiveModifiers] Missing hp_healing_per_turn for HOT effect:', effect);
        } else if (healing !== 0) {
          modifiers.push({
            value: healing,  // Positive because it's healing
            source: effect.source,
            effectId: effect.effect_id,
            effectName: effect.effect_name || 'Unknown Effect',
            duration: effect.turns_remaining,
            max_turns: effect.max_turns,  // Maximum duration for X/Y display
            unlimited: effect.is_permanent,
            counter_state: effect.counter_state || null  // Include counter state for counter effects
          });
        }
      }
    }
    // Similarly for armor
    else if (statName === 'armor' && effect.effect_category === 'dot' && effect.properties?.armor_damage_per_turn !== undefined) {
      const damage = effect.properties.armor_damage_per_turn;
      if (damage === undefined || damage === null) {
        console.warn('[getActiveModifiers] Missing armor_damage_per_turn for DOT effect:', effect);
      } else if (damage !== 0) {
        modifiers.push({
          value: -damage,  // Negative because it's damage
          source: effect.source,
          effectId: effect.effect_id,
          effectName: effect.effect_name || 'Unknown Effect',
          duration: effect.turns_remaining,
          max_turns: effect.max_turns,  // Maximum duration for X/Y display
          unlimited: effect.is_permanent,
          counter_state: effect.counter_state || null  // Include counter state for counter effects
        });
      }
    }
  });
  
  return modifiers;
}

/**
 * Calculate the total modifier value for a stat
 */
export function getTotalModifier(unit, statName) {
  const modifiers = getActiveModifiers(unit, statName);
  
  let total = 0;
  for (const modifier of modifiers) {
    if (modifier.value === undefined || modifier.value === null) {
      console.warn('[getTotalModifier] Missing value for modifier:', modifier);
    } else {
      total += modifier.value;
    }
  }
  
  return total;
}

/**
 * Get the effective value of a stat including all modifiers
 */
export function getEffectiveStat(unit, statName) {
  const baseStat = getBaseStat(unit, statName);
  const totalModifier = getTotalModifier(unit, statName);
  
  // Apply modifiers
  let effectiveValue = baseStat + totalModifier;
  
  // Stats that shouldn't go below 0
  if (['hp', 'max_hp', 'max_ap', 'max_movement', 'agility', 'vision', 'armor'].includes(statName)) {
    effectiveValue = Math.max(0, effectiveValue);
  }
  
  return effectiveValue;
}

/**
 * Format stat display with modifiers
 * Returns an object with display text and color class
 */
export function formatStatWithModifier(unit, statName) {
  const base = getBaseStat(unit, statName);
  const modifier = getTotalModifier(unit, statName);
  const effective = getEffectiveStat(unit, statName);
  
  if (modifier === 0) {
    return {
      text: String(base),
      className: '',
      hasModifier: false
    };
  }
  
  // Format: "base+modifier" or "base-modifier"
  const sign = modifier > 0 ? '+' : '';
  const text = `${base}${sign}${modifier}`;
  const className = modifier > 0 ? 'stat-buffed' : 'stat-debuffed';
  
  return {
    text,
    className,
    hasModifier: true,
    base,
    modifier,
    effective
  };
}

/**
 * Get detailed modifier breakdown for tooltips
 */
export function getModifierBreakdown(unit, statName) {
  const modifiers = getActiveModifiers(unit, statName);
  const base = getBaseStat(unit, statName);
  const total = getTotalModifier(unit, statName);
  const effective = getEffectiveStat(unit, statName);
  
  return {
    base,
    modifiers: modifiers.map(mod => ({
      value: mod.value,
      source: mod.source,
      effectId: mod.effectId,  // Pass through effect ID
      effectName: mod.effectName,  // Keep for backwards compatibility
      sourceUnit: mod.source_unit_id,
      duration: mod.duration,
      max_turns: mod.max_turns,  // Maximum duration for X/Y display
      unlimited: mod.unlimited === true,
      counter_state: mod.counter_state,  // Pass through counter state
      appliedTurn: mod.applied_turn,
      expiresTurn: mod.expires_turn
    })),
    total,
    effective
  };
}